import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, ChevronRight, Store, Clock } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import MenuButton from "@/components/mobile/MenuButton";

export default function MobileMessages() {
  const { t, lang } = useT();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/messages/inbox")
      .then(({ data }) => alive && setThreads(data?.threads || []))
      .catch(() => alive && setThreads([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "ml" ? "ml-IN" : "en-GB");
    } catch {
      return "";
    }
  };

  return (
    <div className="px-5 pt-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("messages.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("messages.subtitle")}</p>
        </div>
        <MenuButton />
      </div>

      <div className="h-px bg-border my-5" />

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-secondary animate-pulse" />
          ))}
        </div>
      ) : threads.length === 0 ? (
        <div className="grid place-items-center py-24 text-center">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-secondary text-primary mb-4">
            <MessageSquare className="w-7 h-7" />
          </span>
          <p className="font-display font-bold text-xl">{t("messages.none")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("messages.startHint")}</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="messages-thread-list">
          {threads.map((th) => (
            <li key={`${th.shop_id}-${th.customer_id || th.user_id || ""}`}>
              <button
                data-testid={`thread-${th.shop_id}`}
                onClick={() => nav(`/shops/${th.shop_id}`)}
                className="w-full text-left flex items-center gap-3 rounded-2xl border border-border bg-card px-3.5 py-3 hover:border-primary/40 transition-colors"
              >
                <span className="grid place-items-center w-12 h-12 rounded-full bg-primary text-primary-foreground shrink-0">
                  <Store className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-extrabold uppercase truncate">{th.shop_name}</p>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3" /> {fmtDate(th.last_at)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate mt-0.5">
                    <span className="font-semibold">
                      {th.last_from === "owner" ? t("messages.shop") : t("messages.you")}{" "}
                    </span>
                    {th.last_text}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
